/**
 * Fertile-Track access from the browser now goes through the Femcare.life web-backend adapter.
 * The browser never sees Fertile-Track's access or refresh tokens — an HttpOnly `fc_sid` cookie
 * is the ONLY auth material on the client. All requests set `credentials: "include"`.
 */
const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

async function request(path, { method = "GET", body, headers = {} } = {}) {
    const h = { "Content-Type": "application/json", ...headers };
    const res = await fetch(`${API_BASE}${path}`, {
        method,
        headers: h,
        body: body ? JSON.stringify(body) : undefined,
        credentials: "include",
    });
    const text = await res.text();
    let data = null;
    try { data = text ? JSON.parse(text) : null; } catch { data = text; }
    if (!res.ok) {
        // FastAPI validation errors return `detail` as an array of {loc, msg, type}.
        // Convert them to a readable string instead of falling back to "HTTP <status>".
        let msg;
        const d = data?.detail;
        if (Array.isArray(d)) {
            msg = d.map((e) => {
                if (!e || typeof e !== "object") return String(e);
                const loc = Array.isArray(e.loc) ? e.loc.filter((p) => p !== "body").join(".") : "";
                return loc ? `${loc}: ${e.msg || e.type || "invalid"}` : (e.msg || e.type || JSON.stringify(e));
            }).join("; ");
        } else if (typeof d === "string") {
            msg = d;
        } else if (data?.message) {
            msg = data.message;
        } else {
            msg = `HTTP ${res.status}`;
        }
        const err = new Error(msg);
        err.status = res.status; err.data = data;
        throw err;
    }
    return data;
}

// Proxy helper — talks to `/api/web/ft/<path-under-fertile-track>`
const proxy = (path, opts) => request(`/api/web/ft${path}`, opts);

/**
 * Normalizes Fertile-Track's /api/subscription/plans response into a stable shape:
 *   { plans: Array<canonical plan>, features: {free:[], premium:[]}, trialDays: number }
 * FT publishes:
 *   { plans: [...], features: {free, premium, ...}, trial_days: 7 }
 * Web callers should only ever consume the normalized shape.
 */
export function normalizePlansResponse(res) {
    const body = res?.data ?? res;
    let plans = [];
    if (Array.isArray(body))           plans = body;
    else if (Array.isArray(body?.plans)) plans = body.plans;
    else if (Array.isArray(body?.items)) plans = body.items;
    const features = (body && typeof body === "object" && body.features) || {};
    const trialDays = (body && body.trial_days) || 0;
    return { plans, features, trialDays };
}

export const ft = {
    // ---- Auth via adapter (no tokens in browser) ----
    async login(email, password)   { return request("/api/web/auth/login",  { method: "POST", body: { email, password } }); },
    async register(payload)        { return request("/api/web/auth/signup", { method: "POST", body: payload }); },
    async phoneSendOtp(phone)      { return request("/api/web/auth/phone/send-otp",   { method: "POST", body: { phone } }); },
    async phoneVerifyOtp(phone, code) { return request("/api/web/auth/phone/verify-otp", { method: "POST", body: { phone, code } }); },
    async firebasePhoneVerify(idToken, extras = {}) {
        // extras may include: link_email, full_name, email, password, dob
        // (mobile-parity signup fields). Backend saves them into the canonical FT profile.
        const body = { id_token: idToken, ...extras };
        return request("/api/web/auth/firebase-phone-verify", { method: "POST", body });
    },
    forgotPassword(email) { return request("/api/web/auth/forgot-password", { method: "POST", body: { email } }); },
    resetPassword(token, password) { return request("/api/web/auth/reset-password", { method: "POST", body: { token, password } }); },
    async me()                     { return request("/api/web/auth/me"); },
    async logout()                 { return request("/api/web/auth/logout", { method: "POST" }); },

    // ---- Product endpoints (proxied server-side with canonical Bearer) ----
    getProfile:            ()      => proxy("/api/profile"),
    putProfile:            (p)     => proxy("/api/profile",             { method: "PUT",  body: p }),
    onboarding:            (b)     => proxy("/api/profile/onboarding",  { method: "POST", body: b }),
    changePassword:        (b)     => proxy("/api/auth/change-password",{ method: "POST", body: b }),
    deleteAccount:         ()      => proxy("/api/auth/account",        { method: "DELETE" }),

    // Cycles
    cyclesCurrent:         ()      => proxy("/api/cycles/current"),
    cyclesHistory:         ()      => proxy("/api/cycles/history"),
    logPeriod:             (b)     => proxy("/api/cycles/log-period",   { method: "POST", body: b }),

    // Calendar
    calendar:              (y, m)  => proxy(`/api/calendar/${y}/${m}`),

    // Logs (daily)
    putLog:                (b)     => proxy("/api/logs",                { method: "POST", body: b }),
    getLog:                (d)     => proxy(`/api/logs/${d}`),
    getLogRange:           (s, e)  => proxy(`/api/logs/range/${s}/${e}`),
    clearLogFlow:          (d)     => proxy(`/api/logs/${d}/flow`,      { method: "DELETE" }),

    // Insights + Trends
    insights:              ()      => proxy("/api/insights"),
    generateInsights:      ()      => proxy("/api/insights/generate",   { method: "POST" }),
    trendsCycles:          ()      => proxy("/api/trends/cycles"),
    trendsMood:            ()      => proxy("/api/trends/mood"),
    trendsSymptoms:        ()      => proxy("/api/trends/symptoms"),
    trendsTemperature:     ()      => proxy("/api/trends/temperature"),
    trendsWeight:          ()      => proxy("/api/trends/weight"),

    // Pregnancy
    pregnancyCurrent:      ()      => proxy("/api/pregnancy/current"),
    pregnancyStart:        (b)     => proxy("/api/pregnancy/start",     { method: "POST", body: b }),
    pregnancyEnd:          (b)     => proxy("/api/pregnancy/end",       { method: "POST", body: b }),
    pregnancyBirthPlan:    ()      => proxy("/api/pregnancy/birth-plan"),
    pregnancyBirthPlanSave:(b)     => proxy("/api/pregnancy/birth-plan",{ method: "PUT", body: b }),
    pregnancyContractionSessions: () => proxy("/api/pregnancy/contraction-sessions"),
    pregnancyContractionSave: (b)  => proxy("/api/pregnancy/contraction-session", { method: "POST", body: b }),
    pregnancyScanGuide:    ()      => proxy("/api/pregnancy/scan-guide"),

    // AI Coach
    aiChat:                (m, s)  => proxy("/api/ai/chat",             { method: "POST", body: { message: m, session_id: s } }),
    aiChatHistory:         ()      => proxy("/api/ai/chat/history"),
    aiChatClear:           ()      => proxy("/api/ai/chat/clear",       { method: "DELETE" }),

    // Daughter Care (full)
    daughterAccess:        ()      => proxy("/api/daughter-care/access"),
    daughterProfiles:      ()      => proxy("/api/daughter-care/profiles"),
    daughterCreateProfile: (b)     => proxy("/api/daughter-care/profiles", { method: "POST", body: b }),
    daughterUpdateProfile: (pid,b) => proxy(`/api/daughter-care/profiles/${pid}`, { method: "PUT", body: b }),
    daughterDeleteProfile: (pid)   => proxy(`/api/daughter-care/profiles/${pid}`, { method: "DELETE" }),
    daughterModule:        (pid)   => proxy(`/api/daughter-care/profiles/${pid}/module`),
    daughterAddCycle:      (pid,b) => proxy(`/api/daughter-care/profiles/${pid}/cycle`, { method: "POST", body: b }),
    daughterAddHealth:     (pid,b) => proxy(`/api/daughter-care/profiles/${pid}/health`, { method: "POST", body: b }),
    daughterReport:        (pid)   => proxy(`/api/daughter-care/profiles/${pid}/report`),
    daughterPairMe:        ()      => proxy("/api/daughter-care/pair/me"),
    daughterPairRedeem:    (b)     => proxy("/api/daughter-care/pair/redeem", { method: "POST", body: b }),
    daughterCreatePairCode:(pid)   => proxy(`/api/daughter-care/profiles/${pid}/pair-code`, { method: "POST" }),
    daughterPairStatus:    (pid)   => proxy(`/api/daughter-care/profiles/${pid}/pair-status`),
    daughterUnlinkPair:    (pid)   => proxy(`/api/daughter-care/profiles/${pid}/pair`, { method: "DELETE" }),
    daughterAiChat:        (b)     => proxy("/api/daughter-care/ai-chat", { method: "POST", body: b }),

    // Community (full)
    communityCategories:   ()      => proxy("/api/community/categories"),
    communityPosts:        (q="")  => proxy(`/api/community/posts${q}`),
    communityCreatePost:   (b)     => proxy("/api/community/posts", { method: "POST", body: b }),
    communityGetPost:      (id)    => proxy(`/api/community/posts/${id}`),
    communityDeletePost:   (id)    => proxy(`/api/community/posts/${id}`, { method: "DELETE" }),
    communityLike:         (id)    => proxy(`/api/community/posts/${id}/like`, { method: "POST" }),
    communityComment:      (id,b)  => proxy(`/api/community/posts/${id}/comment`, { method: "POST", body: b }),
    communityDeleteComment:(id)    => proxy(`/api/community/comments/${id}`, { method: "DELETE" }),
    communityGroups:       ()      => proxy("/api/community/groups"),
    communityCreateGroup:  (b)     => proxy("/api/community/groups", { method: "POST", body: b }),
    communityGetGroup:     (id)    => proxy(`/api/community/groups/${id}`),
    communityDeleteGroup:  (id)    => proxy(`/api/community/groups/${id}`, { method: "DELETE" }),
    communityJoinGroup:    (id)    => proxy(`/api/community/groups/${id}/join`, { method: "POST" }),
    communityLeaveGroup:   (id)    => proxy(`/api/community/groups/${id}/join`, { method: "DELETE" }),
    communityTrending:     ()      => proxy("/api/community/trending"),
    communityDigest:       ()      => proxy("/api/community/digest"),

    // Subscription
    plans:                 ()      => proxy("/api/subscription/plans"),
    pricingContext:        ()      => request("/api/web/pricing/context"),
    subscription:          ()      => proxy("/api/subscription"),
    cancelSubscription:    (b)     => proxy("/api/subscription/cancel", { method: "POST", body: b }),
    startTrial:            (b)     => proxy("/api/subscription/start-trial", { method: "POST", body: b }),
    resumeSubscription:    ()      => proxy("/api/subscription/resume", { method: "POST" }),
    subscriptionReport:    ()      => proxy("/api/subscription/report"),

    // Streaks + Medications + Notifications + Partner + Wearables + Export
    streak:                ()      => proxy("/api/streaks/current"),
    medications:           ()      => proxy("/api/medications"),
    createMedication:      (b)     => proxy("/api/medications", { method: "POST", body: b }),
    deleteMedication:      (id)    => proxy(`/api/medications/${id}`, { method: "DELETE" }),
    takeMedication:        (id)    => proxy(`/api/medications/${id}/take`, { method: "POST" }),
    notifSettings:         ()      => proxy("/api/notifications/settings"),
    updateNotifSettings:   (b)     => proxy("/api/notifications/settings", { method: "PUT", body: b }),
    personalizedTips:      ()      => proxy("/api/notifications/personalized-tips"),
    partnerStatus:         ()      => proxy("/api/partner/status"),
    partnerMe:             ()      => proxy("/api/partner/me"),
    partnerInvite:         (b)     => proxy("/api/partner/invite", { method: "POST", body: b }),
    partnerRedeem:         (b)     => proxy("/api/partner/redeem", { method: "POST", body: b }),
    partnerRemove:         ()      => proxy("/api/partner/remove", { method: "DELETE" }),
    partnerCalendar:       ()      => proxy("/api/partner/read/calendar"),
    partnerHome:           ()      => proxy("/api/partner/read/home"),
    partnerInsights:       ()      => proxy("/api/partner/read/insights"),
    wearablesStatus:       ()      => proxy("/api/wearables/status"),
    wearablesLatest:       ()      => proxy("/api/wearables/latest"),
    wearablesConnect:      (b)     => proxy("/api/wearables/connect", { method: "POST", body: b }),
    wearablesDisconnect:   ()      => proxy("/api/wearables/disconnect", { method: "POST" }),
    wearablesSync:         ()      => proxy("/api/wearables/sync", { method: "POST" }),
    healthSyncStatus:      ()      => proxy("/api/health-sync/status"),
    healthSyncImport:      (b)     => proxy("/api/health-sync/import", { method: "POST", body: b }),
    exportCycleData:       ()      => proxy("/api/export/cycle-data"),
    i18n:                  (lang="en") => proxy(`/api/i18n/translations/${lang}`),

    // ---- Attribution (informational; never controls entitlement) ----
    async attachAttribution(payload) { return request("/api/web/attribution/attach", { method: "POST", body: payload }); },

    // ---- Razorpay TEST-MODE bridge ----
    razorpayConfig:        ()      => request("/api/web/razorpay/config"),
    razorpayCreateOrder:   (planId)=> request("/api/web/razorpay/create-order",  { method: "POST", body: { plan_id: planId } }),
    razorpayVerifyPayment: (b)     => request("/api/web/razorpay/verify-payment",{ method: "POST", body: b }),
    razorpayRetryEntitlement: ()   => request("/api/web/razorpay/retry-entitlement", { method: "POST" }),
};
